import { SOURCE_ATTRIBUTE_KEYS } from 'dashboard/components-next/Forms/sourceAttributeKeys';

// Campos que o formulário pode oferecer. A chave é "origem|escopo|atributo" (a mesma codificação
// das ações de automação) e precisa estar na lista aceita por Forms::ConfigValidator, no backend.
export const STANDARD_FIELDS = [
  {
    group: 'CONTACT',
    key: 'contact|standard|name',
    labelKey: 'NAME',
    type: 'text',
  },
  {
    group: 'CONTACT',
    key: 'contact|standard|phone_number',
    labelKey: 'PHONE',
    type: 'phone',
  },
  {
    group: 'CONTACT',
    key: 'contact|standard|email',
    labelKey: 'EMAIL',
    type: 'email',
  },
  {
    group: 'CONTACT',
    key: 'contact|standard|city',
    labelKey: 'CITY',
    type: 'text',
  },
  {
    group: 'CONTACT',
    key: 'contact|standard|state',
    labelKey: 'STATE',
    type: 'text',
  },
  {
    group: 'CONTACT',
    key: 'contact|standard|zip_code',
    labelKey: 'ZIP_CODE',
    type: 'text',
  },
  {
    group: 'CONTACT',
    key: 'contact|standard|birth_date',
    labelKey: 'BIRTH_DATE',
    type: 'date',
  },
  {
    group: 'DEAL',
    key: 'deal|standard|name',
    labelKey: 'DEAL_NAME',
    type: 'text',
  },
  {
    group: 'DEAL',
    key: 'deal|standard|value',
    labelKey: 'DEAL_VALUE',
    type: 'number',
  },
  { group: 'OTHER', key: 'message', labelKey: 'MESSAGE', type: 'text' },
];

const FIELD_TYPE_BY_DISPLAY_TYPE = {
  text: 'text',
  link: 'text',
  number: 'number',
  currency: 'number',
  percent: 'number',
  date: 'date',
  list: 'list',
  checkbox: 'checkbox',
};

// Atributos personalizados do contato/negociação viram campos. Os de origem (UTMs e dados do
// píxel) ficam de fora: eles vêm da própria landing page, não do visitante.
export const customFieldOptions = (attributes, source) =>
  (attributes || [])
    .filter(
      ({ attributeKey, attributeDisplayType }) =>
        !SOURCE_ATTRIBUTE_KEYS.includes(attributeKey) &&
        FIELD_TYPE_BY_DISPLAY_TYPE[attributeDisplayType]
    )
    .map(attribute => ({
      group: `${source.toUpperCase()}_CUSTOM`,
      key: `${source}|custom|${attribute.attributeKey}`,
      label: attribute.attributeDisplayName,
      type: FIELD_TYPE_BY_DISPLAY_TYPE[attribute.attributeDisplayType],
      options: attribute.attributeValues || [],
    }));

export const buildField = (option, label) => ({
  key: option.key,
  label,
  placeholder: '',
  required: false,
  type: option.type,
  options: option.options || [],
});

// Tipos que um campo personalizado NOVO (ainda sem CustomAttributeDefinition) pode assumir no
// builder (US 05, CA 5.1/5.2). Select e Radio guardam as mesmas opções; o backend provisiona os
// dois como um atributo do tipo "list" (CustomAttributeProvisioningService) — a diferença é só
// visual (dropdown x botões de opção).
export const NEW_CUSTOM_FIELD_TYPES = [
  'text',
  'number',
  'date',
  'checkbox',
  'list',
  'radio',
];

// Campo livre: o time escolhe o rótulo e o tipo, sem depender do catálogo do CRM. A resposta não
// tem campo próprio no contato/negociação e vira uma nota (chave "extra|<slug>").
export const FREE_FIELD_TYPES = [
  'text',
  'textarea',
  'email',
  'phone',
  'number',
  'date',
  'list',
  'checkbox',
];

export const slugify = label =>
  (label || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 50) || 'campo';

// Monta um campo personalizado novo a partir do que o gestor digitou no builder: a chave
// "contact|custom|slug" / "deal|custom|slug" é só derivada do rótulo; o backend garante a
// definição formal (CustomAttributeProvisioningService) ao salvar o formulário.
export const buildNewCustomField = ({ scope, label, type, options }) => ({
  key: `${scope}|custom|${slugify(label)}`,
  label,
  placeholder: '',
  required: false,
  type,
  options: options || [],
});

export const buildFreeField = (label, type, existingKeys) => {
  const base = `extra|${slugify(label)}`;
  let key = base;
  for (let n = 2; existingKeys.includes(key); n += 1) key = `${base}_${n}`;
  return buildField({ key, type }, label);
};

export const isFreeField = field => field.key.startsWith('extra|');

const IDENTIFIER_KEYS = [
  'contact|standard|phone_number',
  'contact|standard|email',
];

export const hasIdentifierField = fields =>
  fields.some(field => IDENTIFIER_KEYS.includes(field.key));

export const embedCode = (publicKey, origin) =>
  `<div data-corvus-form="${publicKey}"></div>\n<script src="${origin}/packs/js/forms.js" async></script>`;
